import React, { useState, useRef, useEffect } from "react";
import { Stage, Layer, Image as KonvaImage, Text, Transformer } from "react-konva";
import useImage from "use-image";

const screenWidth = window.innerWidth;
const A4_RATIO = 1.414;
const A4_WIDTH = screenWidth * 0.9;
const A4_HEIGHT = A4_WIDTH * A4_RATIO;

// Helper to get rotated bounding box (safe even if not rotating)
const getClientRect = (node) => {
  const { x, y, width, height, rotation = 0 } = node.getAttrs();
  const rad = (rotation * Math.PI) / 180;

  const cos = Math.cos(rad);
  const sin = Math.sin(rad);

  const corners = [
    { x, y },
    { x: x + width, y },
    { x: x + width, y: y + height },
    { x, y: y + height },
  ].map(p => ({
    x: x + (p.x - x) * cos - (p.y - y) * sin,
    y: y + (p.x - x) * sin + (p.y - y) * cos,
  }));

  const xs = corners.map(c => c.x);
  const ys = corners.map(c => c.y);

  return {
    x: Math.min(...xs),
    y: Math.min(...ys),
    width: Math.max(...xs) - Math.min(...xs),
    height: Math.max(...ys) - Math.min(...ys),
  };
};

const BackgroundImage = () => {
  const [image] = useImage("A4.jpg");
  return <KonvaImage image={image} width={A4_WIDTH} height={A4_HEIGHT} listening={false} />;
};

const Adjustment = () => {
  const stageRef = useRef(null);
  const textRef = useRef(null);
  const trRef = useRef(null);

  const [isSelected, setIsSelected] = useState(false);
  const [stageScale, setStageScale] = useState(1);

  // Initial state: box size + base font size
  const [textBox, setTextBox] = useState({
    x: A4_WIDTH / 2 - 100,
    y: A4_HEIGHT / 2 - 50,
    width: 200,
    height: 100,
    baseFontSize: 32, // This is the "reference" size at width=200
  });

  // Fit A4 to screen
  useEffect(() => {
    const fit = () => {
      const scale = Math.min(1, (window.innerWidth - 40) / A4_WIDTH);
      setStageScale(scale);
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, []);

  // Attach transformer
  useEffect(() => {
    if (isSelected && trRef.current && textRef.current) {
      trRef.current.nodes([textRef.current]);
      trRef.current.getLayer().batchDraw();
    }
  }, [isSelected]);

  const checkDeselect = (e) => {
    const clickedOnEmpty = e.target === e.target.getStage();
    const clickedOnBg = e.target.attrs.image !== undefined;
    if (clickedOnEmpty || clickedOnBg) setIsSelected(false);
  };

  const onSelect = () => setIsSelected(true);

  // Pinch-to-zoom (unchanged)
  const handleTouchMove = (e) => {
    if (e.evt.touches.length !== 2) return;
    e.evt.preventDefault();
    const stage = stageRef.current;
    const [t1, t2] = e.evt.touches;
    const center = { x: (t1.clientX + t2.clientX) / 2, y: (t1.clientY + t2.clientY) / 2 };
    const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);

    if (!stageRef.current.lastDist) stageRef.current.lastDist = dist;
    let scale = stageScale * (dist / stageRef.current.lastDist);
    scale = Math.max(0.5, Math.min(3, scale));

    const pointTo = {
      x: (center.x - stage.x()) / stageScale,
      y: (center.y - stage.y()) / stageScale,
    };

    stage.scale({ x: scale, y: scale });
    stage.position({
      x: center.x - pointTo.x * scale,
      y: center.y - pointTo.y * scale,
    });

    stageRef.current.lastDist = dist;
    stage.batchDraw();
  };

  const handleTouchEnd = () => {
    stageRef.current.lastDist = 0;
    setStageScale(stageRef.current.scaleX());
  };

  // Font size scales with width (you can also use height or area)
  const getCurrentFontSize = () => {
    const ratio = textBox.width / 200; // 200 is the original width when baseFontSize = 32
    return Math.max(8, Math.round(textBox.baseFontSize * ratio));
  };

  // Save position + size after drag/resize
  const handleTransformEnd = () => {
    const node = textRef.current;
    if (!node) return;

    setTextBox(prev => ({
      ...prev,
      x: node.x(),
      y: node.y(),
      width: Math.max(30, node.width()),
      height: Math.max(20, node.height()),
    }));
  };

  // Prevent going outside A4
  const boundBoxFunc = (oldBox, newBox) => {
    const node = textRef.current;
    if (!node) return oldBox;

    // Apply new size temporarily
    node.x(newBox.x);
    node.y(newBox.y);
    node.width(newBox.width);
    node.height(newBox.height);

    const box = getClientRect(node);

    // Reset
    node.x(oldBox.x);
    node.y(oldBox.y);
    node.width(oldBox.width);
    node.height(oldBox.height);

    const padding = 15;
    if (
      box.x < padding ||
      box.y < padding ||
      box.x + box.width > A4_WIDTH - padding ||
      box.y + box.height > A4_HEIGHT - padding
    ) {
      return oldBox;
    }
    return newBox;
  };

  return (
    <div style={{
      width: "100%",
      height: "100vh",
      display: "flex",
      justifyContent: "center",
      alignItems: "flex-start",
      paddingTop: "40px",
      background: "#f5f5f5",
      overflow: "auto",
    }}>
      <div style={{
        boxShadow: "0 15px 35px rgba(0,0,0,0.15)",
        transform: `scale(${stageScale})`,
        transformOrigin: "top center",
      }}>
        <Stage
          width={A4_WIDTH}
          height={A4_HEIGHT}
          ref={stageRef}
          onMouseDown={checkDeselect}
          onTouchStart={checkDeselect}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <Layer>
            <BackgroundImage />

            <Text
              ref={textRef}
              text="12/11/131"
              x={textBox.x}
              y={textBox.y}
              width={textBox.width}
              height={textBox.height}
              fontSize={getCurrentFontSize()}
              fontFamily="Arial"
              fill="#1a1a1a"
              align="center"
              verticalAlign="middle"
              draggable
              onClick={onSelect}
              onTap={onSelect}
              onDragEnd={handleTransformEnd}
              onTransformEnd={handleTransformEnd}
            />

            {isSelected && (
              <Transformer
                ref={trRef}
                boundBoxFunc={boundBoxFunc}
                rotateEnabled={false}
                enabledAnchors={[
                  "top-left", "top-center", "top-right",
                  "middle-left", "middle-right",
                  "bottom-left", "bottom-center", "bottom-right"
                ]}
                keepRatio={false}
                borderStroke="#0066ff"
                anchorStroke="#0066ff"
                anchorFill="#fff"
                anchorSize={10}
                padding={8}
              />
            )}
          </Layer>
        </Stage>
      </div>
    </div>
  );
};

export default Adjustment;